import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Users } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// v8.3 — self-contained "known issues" banner. Fetches its own data and
// renders nothing (returns null) if there's nothing active or the request
// fails, so dropping it into a page can never break that page's layout or
// its other data-loading. Lets a requester subscribe to an already-known
// problem instead of raising a near-duplicate ticket, reusing the same
// ticket_watchers flow as the pre-submit similar-ticket check.
export default function KnownIssues() {
  const [issues, setIssues] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [trackedIds, setTrackedIds] = useState(new Set());

  useEffect(() => {
    let alive = true;
    api.knownIssues()
      .then((r) => { if (alive) setIssues(r?.issues || []); })
      .catch(() => { /* best-effort only — banner just doesn't render */ })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  if (!loaded || issues.length === 0) return null;

  const track = async (issue) => {
    setBusyId(issue.problem_id);
    try {
      await api.watchTicket(issue.ticket.id);
      setTrackedIds((prev) => new Set(prev).add(issue.problem_id));
      toast.success(`You're now tracking ${issue.ticket.ticket_no}`, {
        description: "We'll notify you as it's updated — no need to raise a new ticket.",
      });
    } catch (e) {
      toast.error(e.message || "Could not subscribe to that issue");
    } finally { setBusyId(null); }
  };

  return (
    <div className="glass rounded-2xl p-5" data-testid="known-issues-banner">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
        <div>
          <div className="overline">Before you raise a ticket</div>
          <h3 className="font-display text-xl font-extrabold tracking-tighter">
            {issues.length === 1 ? "1 known issue right now" : `${issues.length} known issues right now`}
          </h3>
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {issues.map((issue) => {
          const tracked = trackedIds.has(issue.problem_id);
          return (
            <div key={issue.problem_id} className="flex items-center justify-between gap-3 rounded-xl bg-white/70 dark:bg-white/5 border p-3">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{issue.title}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                  <Users className="h-3 w-3" /> {issue.affected_count} report{issue.affected_count === 1 ? "" : "s"}
                  {issue.workaround ? <span> · workaround available</span> : null}
                </div>
                {issue.workaround && (
                  <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{issue.workaround}</p>
                )}
              </div>
              <Button type="button" size="sm" disabled={busyId === issue.problem_id || tracked}
                onClick={() => track(issue)}
                className="rounded-full h-8 shrink-0 bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60">
                {tracked ? "Tracking" : busyId === issue.problem_id ? "Subscribing…" : "Track this instead"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
